import React, { useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import BuildIcon from "@mui/icons-material/Build";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import firebase from "firebase/compat/app";
import "firebase/compat/firestore";
import DashboardPageHeader from "../components/DashboardPageHeader";
import { useAuth } from "../../auth/hooks/useAuth";
import { useOrganization } from "../../auth/hooks/useOrganization";
import { useExercisesMap } from "../../plan/hooks/useExercisesMap";
import {
  collectOccurrencesMatchingExercise,
  replaceExerciseIdsInReportData,
  sanitizeForFirestore,
  summarizeRecordedSetsForMergeLeaf,
} from "../lib/workoutReportMutations";

function chunk(arr, size) {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

function toJsDate(value) {
  if (!value) return null;
  if (typeof value.toDate === "function") return value.toDate();
  if (value instanceof Date) return value;
  try {
    return new Date(value);
  } catch {
    return null;
  }
}

function formatWorkoutRowDate(docData) {
  const d =
    toJsDate(docData?.completedOn) ??
    toJsDate(docData?.createdOn);
  return d instanceof Date && !Number.isNaN(d.getTime()) ? d.toLocaleString(undefined, {
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "numeric",
  }) : "—";
}

function workoutTimestampMillis(docData) {
  const d =
    toJsDate(docData?.completedOn) ??
    toJsDate(docData?.createdOn);
  return d instanceof Date && !Number.isNaN(d.getTime()) ? d.getTime() : 0;
}

export default function WorkoutToolsPage() {
  const { organizationId, clientId } = useAuth();
  const { exercises, loading } = useExercisesMap(organizationId, !!organizationId);
  const { weightUnit } = useOrganization(organizationId);

  const [fromExercise, setFromExercise] = useState(null);
  const [toExercise, setToExercise] = useState(null);

  const [previewScanning, setPreviewScanning] = useState(false);
  /** @type {[Array<{docId:string, ref:FirebaseFirestore.DocumentReference, selected:boolean, dateLabel:string, splitName:string, occurrences:{path:string, lines:string[]}[]}>>|null, Function]} */
  const [previewRows, setPreviewRows] = useState(null);
  const [previewError, setPreviewError] = useState(null);

  const [migrateBusy, setMigrateBusy] = useState(false);
  const [migrateAlert, setMigrateAlert] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [progress, setProgress] = useState(0);

  const sameSelection = Boolean(
    fromExercise?.id &&
      toExercise?.id &&
      String(fromExercise.id) === String(toExercise.id),
  );

  const selectedCount =
    previewRows?.filter((r) => r.selected).length ?? 0;
  const headerBulkChecked =
    Boolean(previewRows?.length && previewRows.every((r) => r.selected));
  const headerBulkIndeterminate =
    Boolean(previewRows?.length && previewRows.some((r) => r.selected) && !headerBulkChecked);

  const migrateSummary = useMemo(() => {
    if (!selectedCount || !fromExercise?.name || !toExercise?.name || sameSelection) return "";
    return `Rewrite ${selectedCount} selected workout record(s), replacing "${fromExercise.name}" with "${toExercise.name}"`;
  }, [fromExercise?.name, sameSelection, selectedCount, toExercise?.name]);

  const assembleMergeCandidatesFromFirestore = async () => {
    const snap = await firebase
      .firestore()
      .collection("organizations")
      .doc(organizationId)
      .collection("clients")
      .doc(clientId)
      .collection("reportExercise")
      .get();

    const rows = [];

    snap.docs.forEach((doc) => {
      const data = doc.data() ?? {};
      const rd = data.reportData;
      const occurrencesRaw = collectOccurrencesMatchingExercise(rd, fromExercise.id);
      if (!occurrencesRaw.length) return;

      const occurrences = occurrencesRaw.map((hit) => ({
        path: hit.path,
        lines: summarizeRecordedSetsForMergeLeaf(hit.leaf, weightUnit ?? "lbs"),
      }));

      rows.push({
        docId: doc.id,
        ref: doc.ref,
        selected: true,
        dateLabel: formatWorkoutRowDate(data),
        sortMillis: workoutTimestampMillis(data),
        splitName: rd?.split?.name ?? rd?.protocol?.name ?? "",
        occurrences,
      });
    });

    rows.sort((a, b) => b.sortMillis - a.sortMillis);

    return rows;
  };

  const refreshMergePreviewList = async ({ clearSuccessBanner = false, resetRowsFirst = false } = {}) => {
    setPreviewError(null);

    if (clearSuccessBanner) {
      setMigrateAlert(null);
    }

    if (!organizationId || !clientId || !fromExercise?.id) {
      const message = "Choose the exercise you are replacing before loading the checklist.";
      setPreviewError(message);
      if (!fromExercise?.id && resetRowsFirst) {
        setPreviewRows(null);
      }
      return null;
    }

    if (resetRowsFirst) {
      setPreviewRows(null);
    }

    setPreviewScanning(true);
    try {
      const rows = await assembleMergeCandidatesFromFirestore();
      setPreviewRows(rows);
      return rows;
    } catch (e) {
      const message = e?.message ?? "Failed to refresh workout history from Firestore.";
      setPreviewError(message);
      return null;
    } finally {
      setPreviewScanning(false);
    }
  };

  const runPreviewScan = async () => {
    await refreshMergePreviewList({ clearSuccessBanner: true, resetRowsFirst: true });
  };
  const handleToggleBulkSelect = () => {
    setPreviewRows((prev) => {
      if (!prev?.length) {
        return prev;
      }
      const selectingAll = !prev.every((r) => r.selected);
      return prev.map((r) => ({ ...r, selected: selectingAll }));
    });
  };

  const toggleRowSelection = (docId) => {
    setPreviewRows((prev) =>
      prev?.map((r) => (r.docId === docId ? { ...r, selected: !r.selected } : r)) ?? null,
    );
  };

  const performMigration = async () => {
    const selectedRefs = previewRows?.filter((r) => r.selected) ?? [];
    if (!organizationId || !clientId || !fromExercise?.id || !toExercise?.id || sameSelection) {
      setMigrateAlert({ severity: "warning", message: "Pick two different exercises first." });
      return;
    }
    if (!selectedRefs.length) {
      setMigrateAlert({ severity: "warning", message: "Select at least one workout session to migrate." });
      return;
    }

    const fromId = fromExercise.id;
    const toId = toExercise.id;
    const canonicalName = toExercise.name ?? String(toId);

    setMigrateBusy(true);
    setMigrateAlert(null);
    setProgress(0);

    try {
      const fetchResults = await Promise.all(
        selectedRefs.map(async ({ ref }) => {
          const snap = await ref.get();
          if (!snap.exists) {
            return null;
          }
          const data = snap.data() ?? {};
          const { reportData, changed } = replaceExerciseIdsInReportData(
            data.reportData ?? {},
            fromId,
            toId,
            canonicalName,
          );
          if (!changed) {
            return null;
          }
          return { ref: snap.ref, reportData };
        }),
      );
      const updates = fetchResults.filter(Boolean);

      if (!updates.length) {
        const refreshedRows = await refreshMergePreviewList({ clearSuccessBanner: false, resetRowsFirst: false });
        const remaining = refreshedRows?.length ?? null;
        setMigrateAlert({
          severity: "warning",
          message:
            remaining === null
              ? `Nothing was updated (${selectedRefs.length} checked). Reloading failed — tap Preview workouts to retry.`
              : `Nothing was saved (${selectedRefs.length} checked). After refreshing, ${remaining} workout record${remaining === 1 ? "" : "s"} still reference "${fromExercise.name}".`
        });
        setConfirmOpen(false);
        return;
      }

      let done = 0;
      const groups = chunk(updates, 400);
      const totalOps = updates.length || 1;
      for (const batchOps of groups) {
        const batch = firebase.firestore().batch();
        batchOps.forEach(({ ref, reportData }) =>
          batch.update(ref, {
            reportData: sanitizeForFirestore(reportData),
            updatedOn: firebase.firestore.FieldValue.serverTimestamp(),
          }),
        );
        await batch.commit();
        done += batchOps.length;
        setProgress(Math.min(100, Math.round((done / totalOps) * 100)));
      }

      const dropped = Math.max(selectedRefs.length - updates.length, 0);

      const refreshedRows = await refreshMergePreviewList({ clearSuccessBanner: false, resetRowsFirst: false });

      const parts = [
        `Successfully updated ${updates.length} workout record${updates.length === 1 ? "" : "s"}.`,
      ];

      if (dropped > 0) {
        parts.push(
          `${dropped} checked session${dropped === 1 ? "" : "s"} no longer referenced that catalog id at save time—those files were skipped.`,
        );
      }

      if (refreshedRows === null) {
        parts.push("Checklist reload failed automatically — tap Preview workouts to reconcile the UI.");
      } else if (refreshedRows.length === 0) {
        parts.push(`Checklist refreshed — zero workouts still point at "${fromExercise.name}" with this catalog id.`);
      } else {
        parts.push(
          `Checklist refreshed — ${refreshedRows.length} session${refreshedRows.length === 1 ? "" : "s"} below still lists that outgoing id for review.`
        );
      }

      setMigrateAlert({ severity: "success", message: parts.join(" ") });
      setConfirmOpen(false);
    } catch (e) {
      setMigrateAlert({
        severity: "error",
        message: e?.message ?? "Migration failed. Try again or contact support.",
      });
    } finally {
      setMigrateBusy(false);
      setProgress(100);
    }
  };

  const previewTotal = previewRows?.length ?? 0;

  return (
    <Box sx={{ maxWidth: 980, mx: "auto" }}>
      <DashboardPageHeader
        title="Workout history tools"
        subtitle="Rewrite historical lifts when you picked the wrong catalog exercise—for example consolidating two shoulder press variants. Session edits stay on the dashboard calendar."
        icon={<BuildIcon />}
      />

      {!organizationId || !clientId ? (
        <Alert severity="info" sx={{ mt: 3 }}>
          Sign in as a client to access these tools.
        </Alert>
      ) : (
        <>
          <Card elevation={2} sx={{ mt: 2 }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Stack spacing={2}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <SyncAltIcon color="secondary" />
                  <Typography variant="h6" color="secondary" sx={{ fontWeight: 700 }}>
                    Merge exercise IDs
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Preview lists every workout that references the outgoing exercise ID. Review recorded sets (weights/reps) for each session,
                  uncheck outliers, then migrate only what you approve.
                </Typography>
                <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                  <Autocomplete
                    fullWidth
                    options={exercises}
                    loading={loading}
                    disabled={migrateBusy || loading || previewScanning}
                    value={fromExercise}
                    onChange={(_, v) => {
                      setFromExercise(v ?? null);
                      setPreviewRows(null);
                    }}
                    getOptionLabel={(opt) => (opt?.name ? `${opt.name} (${opt.id})` : "")}
                    isOptionEqualToValue={(a, b) => !!a?.id && a.id === b?.id}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Replace this catalog exercise"
                        placeholder="Exercise you mistakenly logged under"
                      />
                    )}
                  />
                  <Autocomplete
                    fullWidth
                    options={exercises}
                    loading={loading}
                    disabled={migrateBusy || loading || previewScanning}
                    value={toExercise}
                    onChange={(_, v) => setToExercise(v ?? null)}
                    getOptionLabel={(opt) => (opt?.name ? `${opt.name} (${opt.id})` : "")}
                    isOptionEqualToValue={(a, b) => !!a?.id && a.id === b?.id}
                    renderInput={(params) => (
                      <TextField {...params} label="Combine into this exercise" placeholder="Canonical movement" />
                    )}
                  />
                </Stack>

                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }}>
                  <Button
                    variant="outlined"
                    disabled={migrateBusy || previewScanning || !fromExercise?.id}
                    onClick={runPreviewScan}
                  >
                    {previewScanning
                      ? migrateBusy
                        ? "Refreshing checklist…"
                        : "Scanning history…"
                      : "Preview workouts"}
                  </Button>
                  <Button
                    variant="contained"
                    color="secondary"
                    disabled={
                      migrateBusy ||
                      previewScanning ||
                      !fromExercise?.id ||
                      !toExercise?.id ||
                      sameSelection ||
                      selectedCount === 0
                    }
                    onClick={() => setConfirmOpen(true)}
                  >
                    Migrate selected ({selectedCount})
                  </Button>
                  {migrateBusy ? (
                    <LinearProgress sx={{ flex: 1, minHeight: 6, borderRadius: 1 }} value={progress} variant="determinate" />
                  ) : null}
                </Stack>

                {sameSelection ? <Alert severity="warning">Pick two different catalog exercises.</Alert> : null}
                {previewError ? <Alert severity="error">{previewError}</Alert> : null}

                {!previewScanning && previewRows && previewRows.length === 0 ? (
                  <Alert severity="success">{`No workouts currently reference "${fromExercise?.name ?? "that exercise"}".`}</Alert>
                ) : null}

                {!previewScanning && previewRows && previewRows.length > 0 ? (
                  <Paper variant="outlined" sx={{ p: { xs: 1.25, sm: 2 }, borderRadius: 2 }}>
                    <Stack spacing={2}>
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        alignItems={{ sm: "center" }}
                        justifyContent="space-between"
                        spacing={1}
                      >
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                          {previewTotal} workout records reference this exercise ID
                        </Typography>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={headerBulkChecked}
                              indeterminate={headerBulkIndeterminate}
                              onChange={handleToggleBulkSelect}
                            />
                          }
                          label="Select / deselect all"
                        />
                      </Stack>

                      <Stack spacing={1.75}>
                        {previewRows.map((row) => (
                          <Paper
                            key={row.docId}
                            elevation={0}
                            sx={{
                              border: "1px solid",
                              borderColor: "divider",
                              borderRadius: 2,
                              p: 1.5,
                              bgcolor: row.selected ? "background.paper" : "grey.50",
                              opacity: row.selected ? 1 : 0.7,
                            }}
                          >
                            <Stack spacing={1.25}>
                              <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="space-between" alignItems={{ sm: "center" }}>
                                <FormControlLabel
                                  sx={{ mr: "auto", alignItems: "flex-start" }}
                                  control={
                                    <Checkbox
                                      checked={row.selected}
                                      onChange={() => toggleRowSelection(row.docId)}
                                      disabled={migrateBusy}
                                    />
                                  }
                                  label={
                                    <Stack spacing={0.25}>
                                      <Typography variant="body1" sx={{ fontWeight: 700 }}>
                                        {row.dateLabel}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace" }}>
                                        Doc {row.docId.slice(0, 10)}
                                        {row.docId.length > 10 ? "…" : ""}
                                      </Typography>
                                    </Stack>
                                  }
                                />
                                {row.splitName ? <Chip variant="outlined" size="small" label={row.splitName} /> : null}
                              </Stack>

                              <Stack spacing={1.25} sx={{ pl: { xs: 0.5, sm: 4.75 } }}>
                                {row.occurrences.map((occ, idx) => (
                                  <Box key={`${row.docId}-occ-${idx}-${occ.path}`}>
                                    <Typography variant="subtitle2" color="secondary" sx={{ fontWeight: 700 }}>
                                      {occ.path}
                                    </Typography>
                                    <Box component="ul" sx={{ m: 0, pl: 2.75, "& li": { lineHeight: 1.55 } }}>
                                      {(occ.lines.length ? occ.lines : ["No reps/weights recorded for this block."]).map(
                                        (line, lineIdx) => (
                                          <Typography
                                            key={`${row.docId}-occ-${idx}-ln-${lineIdx}`}
                                            component="li"
                                            variant="body2"
                                          >
                                            {line}
                                          </Typography>
                                        ),
                                      )}
                                    </Box>
                                  </Box>
                                ))}
                              </Stack>
                            </Stack>
                          </Paper>
                        ))}
                      </Stack>
                      <Typography variant="caption" color="text.secondary">
                        {`Checked sessions merge into "${toExercise?.name ?? "the target exercise"}" (${toExercise?.id ?? "?"}). Leave sessions unchecked when the recorded weights/reps look like different movements.`}
                      </Typography>
                    </Stack>
                  </Paper>
                ) : null}

                {migrateAlert ? <Alert severity={migrateAlert.severity}>{migrateAlert.message}</Alert> : null}

                <Divider />
                <Typography variant="subtitle2">Tips</Typography>
                <Typography variant="body2" color="text.secondary">
                  Migrating edits the same workout documents the mobile app writes. Charts refresh via realtime listeners. If Firestore denies a write,
                  contact your coach; client accounts normally have permission once authenticated.
                </Typography>
              </Stack>
            </CardContent>
          </Card>

          <Dialog open={confirmOpen} onClose={() => (!migrateBusy ? setConfirmOpen(false) : null)} fullWidth maxWidth="sm">
            <DialogTitle>Rewrite selected workouts?</DialogTitle>
            <DialogContent dividers>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {migrateSummary || `Ready to migrate ${selectedCount} selected session(s).`}
              </Typography>
              <Alert severity="warning">
                Only continue if you are sure matching sets belong to "{toExercise?.name ?? "the canonical exercise"}". Coaches cannot undo this automatically —
                screenshots help if anything looks off later.
              </Alert>
            </DialogContent>
            <DialogActions>
              <Button disabled={migrateBusy} onClick={() => setConfirmOpen(false)}>
                Cancel
              </Button>
              <Button variant="contained" color="secondary" disabled={migrateBusy} onClick={performMigration}>
                {migrateBusy ? "Working…" : `Migrate ${selectedCount} session${selectedCount === 1 ? "" : "s"}`}
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </Box>
  );
}
